import { Elevation, H3, Section, SectionCard } from "@blueprintjs/core";
import { IconName } from "@blueprintjs/icons";
import { PropsWithChildren, useState } from "react";
import css from "./PersistentSection.module.scss";

const SectionTitle: React.FC<React.HTMLAttributes<HTMLElement>> = (props) => (
    <H3 {...props} />
);

/**
 * Collapsible panel that remembers whether it is open, under
 * `storageKey`. The key is read once on mount, so pass a constant
 * rather than something derived from the route.
 */
export const PersistentSection: React.FC<
    PropsWithChildren<{
        title: string;
        /** One-line summary of the contents, shown under the title. */
        summary: string;
        icon: IconName;
        /** localStorage key holding this panel's open state. */
        storageKey: string;
        /** Open state until the viewer first toggles the panel. */
        defaultIsOpen?: boolean;
        /**
         * Rendered right-aligned in the header. A control here must
         * stop click propagation, or it also toggles the panel.
         */
        actions?: JSX.Element;
        /** Rendered above the children, inside the panel. */
        toolbar?: React.ReactNode;
        /** Set false to let the children fill the panel edge to edge. */
        padded?: boolean;
    }>
> = ({
    title,
    summary,
    icon,
    storageKey,
    defaultIsOpen = false,
    actions,
    toolbar,
    padded = true,
    children
}) => {
    const [isOpen, setIsOpen] = useState(() => {
        const stored = localStorage.getItem(storageKey);
        return stored === null ? defaultIsOpen : stored === "true";
    });
    const toggle = () => {
        const next = !isOpen;
        localStorage.setItem(storageKey, String(next));
        setIsOpen(next);
    };

    return (
        <Section
            collapsible
            collapseProps={{ isOpen, onToggle: toggle }}
            icon={icon}
            title={title}
            subtitle={summary}
            titleRenderer={SectionTitle}
            rightElement={actions}
            elevation={Elevation.ONE}
            className={css["PersistentSection"]}
        >
            <SectionCard padded={padded}>
                {toolbar !== undefined && (
                    <div className={css["PersistentSection-toolbar"]}>
                        {toolbar}
                    </div>
                )}
                {children}
            </SectionCard>
        </Section>
    );
};
